import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bot } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { assistentApi, type AssistentEinstellungen as Einstellungen } from '@/lib/assistent/assistent.api'
import { useAssistent } from '@/lib/assistent/assistent.store'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { fmtDate } from '@/lib/format-date'
import { cardTitle, formCard, inlineInput } from '@/lib/form-styles'
import { AssistentGrenzen } from './AssistentGrenzen'
import { AssistentVerbrauch } from './AssistentVerbrauch'

/**
 * AP-18 12. — Karte **„Assistent"** in den KI-Einstellungen: ein- und ausschalten, woher der
 * Schlüssel kommt, und die Einwilligung in den Datenweg.
 *
 * Einschalten und der Wechsel des Schlüssels sind **Handlungen** (`.claude/rules/speichern.md`,
 * Fall 3): ein Knopf mit dem Verb und eine Rückfrage mit dem Text, in den eingewilligt wird — kein
 * Schalter, der beim Umlegen still etwas an einen KI-Anbieter freigibt.
 *
 * Darunter (Phase G, 12.2): die Karte „Grenzen und Datenschutz" — Tagesgrenze je Benutzer,
 * Aufbewahrung, gesperrte Bereiche, feldweise ohne Knopf — und die Karte „Verbrauch".
 */
export function AssistentEinstellungen() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const darfAendern = erfuelltRecht('ai.config.manage', user?.permissions)
  const ladeStand = useAssistent((s) => s.ladeStand)
  const [e, setE] = useState<Einstellungen | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const laden = useCallback(() => {
    void assistentApi
      .einstellungen()
      .then(setE)
      .catch((x) => setFehler(apiErrorText(x, t('assistant.settings.loadError'))))
  }, [t])
  useEffect(laden, [laden])

  async function tu(dto: Parameters<typeof assistentApi.einstellungenAendern>[0]) {
    setLaeuft(true)
    setFehler(null)
    try {
      setE(await assistentApi.einstellungenAendern(dto))
      // Die Seitenleiste soll sofort wissen, ob sie Fragen annimmt.
      void ladeStand()
    } catch (x) {
      setFehler(apiErrorText(x, t('assistant.settings.saveError')))
    } finally {
      setLaeuft(false)
    }
  }

  if (!e) return fehler ? <p className="text-sm text-destructive">{fehler}</p> : null

  const quelle = (k: 'TENANT' | 'CENTRAL') => t(`assistant.settings.key.${k}`)
  const einwilligung = (k: 'TENANT' | 'CENTRAL') => `${e.consentText}\n\n${t('assistant.settings.consentKey', { key: quelle(k) })}`
  const an = e.enabled && e.consentCurrent

  async function einschalten() {
    if (!(await bestaetige(einwilligung(e!.keySource), { ok: t('assistant.settings.confirmOn') }))) return
    await tu({ enabled: true, consent: true })
  }

  async function umstellen(k: 'TENANT' | 'CENTRAL') {
    if (k === e!.keySource) return
    // Ein anderer Schlüssel ist ein anderer Datenweg — bei ausgeschaltetem Assistenten wird er erst beim Einschalten bestätigt.
    if (an) {
      if (!(await bestaetige(einwilligung(k), { ok: t('assistant.settings.confirmSwitch') }))) return
      await tu({ keySource: k, consent: true })
    } else await tu({ keySource: k })
  }

  const zahl = (n: number) => n.toLocaleString('de-DE')
  const prozent = e.quota.limit > 0 ? Math.min(100, Math.round((e.quota.used / e.quota.limit) * 100)) : 0
  return (
    <>
    <div className={formCard}>
      <div className="mb-1 flex items-center gap-2">
        <Bot size={18} className="text-primary" />
        <h2 className={cardTitle}>{t('assistant.settings.title')}</h2>
      </div>
      <p className="text-sm text-muted-foreground">{t('assistant.settings.description')}</p>

      <div className="mt-4 space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span className="w-44 text-muted-foreground">{t('assistant.settings.state')}</span>
          <span className="font-medium">{t(an ? 'assistant.settings.on' : 'assistant.settings.off')}</span>
          {darfAendern &&
            (an ? (
              <button type="button" className="rounded-md border border-border px-3 py-1 text-sm hover:bg-accent disabled:opacity-50" disabled={laeuft} onClick={() => void tu({ enabled: false })}>
                {t('assistant.settings.switchOff')}
              </button>
            ) : (
              <button type="button" className="rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground disabled:opacity-50" disabled={laeuft || !e.aiEnabled} onClick={() => void einschalten()}>
                {t('assistant.settings.switchOn')}
              </button>
            ))}
        </div>
        {e.consentCurrent && e.consentAt && <p className="text-xs text-muted-foreground">{t('assistant.settings.consentedAt', { date: fmtDate(e.consentAt) })}</p>}
        {!e.aiEnabled && <p className="rounded-md bg-amber-500/15 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">{t('assistant.blocked.AI_DISABLED')}</p>}

        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="assistent-schluessel" className="w-44 text-muted-foreground">
            {t('assistant.settings.paidVia')}
          </label>
          <select id="assistent-schluessel" className={`${inlineInput} !w-auto`} value={e.keySource} disabled={!darfAendern || laeuft} onChange={(x) => void umstellen(x.target.value as 'TENANT' | 'CENTRAL')}>
            <option value="TENANT">{quelle('TENANT')}</option>
            <option value="CENTRAL">{quelle('CENTRAL')}</option>
          </select>
        </div>
        {e.keySource === 'TENANT' && !e.tenantKey && <p className="rounded-md bg-amber-500/15 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">{t('assistant.settings.noKeyHint')}</p>}
        {e.keySource === 'CENTRAL' && (
          <p className={`text-xs ${e.quota.exhausted || e.quota.limit <= 0 ? 'text-destructive' : e.quota.warn ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}>
            {e.quota.limit > 0 ? t('assistant.settings.quota', { used: zahl(e.quota.used), limit: zahl(e.quota.limit), percent: prozent }) : t('assistant.blocked.NO_QUOTA')}
          </p>
        )}
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      </div>
    </div>
    <AssistentGrenzen e={e} darfAendern={darfAendern} onGeaendert={setE} />
    <AssistentVerbrauch tagesgrenze={e.dailyTokenLimitPerUser} />
    </>
  )
}
